import { useContext } from 'react';
import { Box, Tooltip, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';

import { PriceGridContext } from '../../context/PriceGridContext';
import { selectProject } from '../../features/project/projectSlice';
import { IUnit } from '../../types/unit';
import { unitStatusTitles } from '../../utils/Constants';
import { IStatus } from '../../types/project';
import {
  convertAllDates,
  effectivePriceAfterRebate,
  hstRebate,
  netHST,
  numToCurrency,
  getOutdoorSizeDisplay,
  getOutdoorType,
  getSalesStatusCodes,
} from '../../utils/Functions';
import { FlexBetween } from '../../commonStyles';
import { useAppDispatch } from '../../app/hooks';
import { handleModal } from '../../features/modal/modalSlice';
import './mediaPrint.css';

const UnitGridCard = (props: ChildProps) => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const { unit, displayed, handleSelectedUnits } = props;
  const { selectingUnits, displayedInfo, selectedUnits, filter, setUnit, setModalType, leasing } = useContext(PriceGridContext);
  const isCompact = useMediaQuery('(max-width:1499.98px)');
  const salesStatusCodes = getSalesStatusCodes(project);

  const handleOpenModal = (e: any, unit: IUnit, type: string) => {
    if (type === 'click') {
      if (!selectingUnits) {
        setUnit(unit);
        setModalType('floorPlan');
        storeDispatch(handleModal(true));
      } else {
        handleSelectedUnits(unit);
      }
    } else if (type === 'hover') {
      if (selectingUnits) {
        if (e.buttons === 1) {
          if (selectedUnits.includes(unit._id)) return;
          handleSelectedUnits(unit);
        }
      }
    }
  };

  const suiteStatus = () => {
    let unitStatusArray: IStatus[] = [...project.status, ...unitStatusTitles];
    let statusObject;
    if (leasing) {
      if (unit.leaseStatus) {
        statusObject = unitStatusArray.find((status: IStatus) => unit.leaseStatus === status.code);
      } else {
        statusObject = {
          code: 'A',
          color: '#fff',
          name: 'Available',
        };
      }
    } else {
      statusObject = unitStatusArray.find((status: IStatus) => unit.status === status.code);
    }

    let bgColour = statusObject?.color;

    if (selectingUnits && !displayed) {
      bgColour = statusObject?.color + '30';
    }

    let statusStyle = {
      status: statusObject?.code,
      backgroundColor: bgColour,
      boxShadow: unit.flag ? '0px 0px 1px 1px red' : '',
    };
    return statusStyle;
  };

  const handleFontSize = (allocation: any) => {
    if (allocation) {
      if (allocation.fullName.length > 18) {
        return isCompact ? '6.8px' : '8.5px';
      } else return isCompact ? '9.6px' : '12px';
    } else return isCompact ? '9.6px' : '12px';
  };

  const handleDisplay = () => {
    if (filter) {
      if (displayed) {
        return 'block';
      } else return 'none';
    } else {
      if (Object.keys(unit).length !== 0) {
        return 'block';
      } else return 'none';
    }
  };

  return (
    <div style={{ height: '100%' }}>
      <Box
        className="suiteCard"
        style={suiteStatus()}
        sx={{
          color: '#000',
          borderRadius: isCompact ? '6.4px' : '8px',
          border: '1px solid #000',
          padding: isCompact ? '6.4px' : '8px',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.1)',
          },
          cursor: 'pointer',
          boxShadow: '0px 0px 1px 1px grey',
          height: '100%',
          fontSize: isCompact ? '9.6px' : '12px',
          display: handleDisplay(),
          userSelect: 'none',
        }}
        onClick={(e) => handleOpenModal(e, unit, 'click')}
        onMouseOver={(e) => handleOpenModal(e, unit, 'hover')}
      >
        {displayedInfo.projectName && (project._id === '65bd404201d843802bb37a51' || leasing) ? (
          <Tooltip title="Project">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
                fontSize: isCompact ? 11.2 : 14,
              }}
            >
              <strong>{unit.projectName ? unit.projectName : ''}</strong>
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.unitNumber ? (
          <FlexBetween>
            <Tooltip title="Unit Number">
              <Box
                sx={{
                  opacity: selectingUnits && !displayed ? '40%' : '100%',
                }}
              >
                <strong>
                  {unit.suite} {unit.status ? `(${suiteStatus().status})` : null}
                </strong>
              </Box>
            </Tooltip>
            {unit.custom ? (
              <Box
                sx={{
                  fontSize: isCompact ? '6.4px' : '8px',
                  fontWeight: 'bold',
                  opacity: selectingUnits && !displayed ? '40%' : '100%',
                }}
              >
                {unit.custom}
              </Box>
            ) : null}
          </FlexBetween>
        ) : null}
        {unit.tier ? (
          <Box
            sx={{
              fontSize: isCompact ? '8px' : '10px',
              fontWeight: 'bold',
              opacity: selectingUnits && !displayed ? '40%' : '100%',
            }}
          >
            {unit.tier}
          </Box>
        ) : null}
        {unit.modelName ? (
          <Box
            sx={{
              fontSize: isCompact ? '8px' : '10px',
              fontWeight: 'bold',
              opacity: selectingUnits && !displayed ? '40%' : '100%',
            }}
          >
            {unit.modelName}
          </Box>
        ) : null}
        {displayedInfo.unitType ? (
          <FlexBetween>
            <Tooltip title="Unit Type">
              <Box
                sx={{
                  opacity: selectingUnits && !displayed ? '40%' : '100%',
                }}
              >
                {unit.unitType}
              </Box>
            </Tooltip>
          </FlexBetween>
        ) : null}
        {displayedInfo.modelType ? (
          <Tooltip title="Model Type">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {unit.modelType}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.bathrooms ? (
          <Tooltip title="Number of Bathrooms">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {unit.bathroom}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.size ? (
          <Tooltip title="Unit Size (sqft)">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {unit.size}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.outdoorSize ? (
          <Tooltip title="Outdoor Size (sqft)">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {getOutdoorSizeDisplay(unit.outdoor)}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.outdoorType ? (
          <Tooltip title="Outdoor Type">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {getOutdoorType(unit.outdoor)}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.exposure ? (
          <Tooltip title="Exposure">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {unit.exposure}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.level ? (
          <Tooltip title="Level">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {unit.level}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.unit ? (
          <Tooltip title="Unit">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {unit.unit}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.address && (unit.address || project.salesOffice) ? (
          <Tooltip title="Address">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {unit.address ? unit.address : project.salesOffice}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.pricePerSquareFoot && !project.netHst && project._id !== '65bd404201d843802bb37a51' && !leasing ? (
          <Tooltip title="Price Per Square Foot">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {unit.basePrice ? numToCurrency.format(unit.basePrice / unit.size) : null}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.basePrice && !project.netHst && project._id !== '65bd404201d843802bb37a51' && !leasing ? (
          <Tooltip title="Base Price">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              <strong>
                {unit.deal && unit.deal.basePrice
                  ? numToCurrency.format(unit.deal.basePrice)
                  : unit.basePrice
                  ? numToCurrency.format(unit.basePrice)
                  : null}
              </strong>
            </Box>
          </Tooltip>
        ) : null}
        <Tooltip title="Effective Price after HST Rebate">
          <Box
            sx={{
              opacity: selectingUnits && !displayed ? '40%' : '100%',
              color: project.netHst ? '#000' : '#488d48',
            }}
          >
            <strong>{numToCurrency.format((effectivePriceAfterRebate(unit.basePrice, project)))}</strong>
          </Box>
        </Tooltip>
        {!salesStatusCodes.includes(unit.status) && !project.netHst ? (
          <Tooltip title="Total HST Rebate Amount">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
                color: '#488d48',
              }}
            >
              <strong>{numToCurrency.format(hstRebate(unit.basePrice, project))}</strong>
            </Box>
          </Tooltip>
        ) : null}
          <Tooltip title="Price Per Square Foot Net">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
                color: '#000',
              }}
            >
              <strong>
                {unit.basePrice && unit.size ? numToCurrency.format(netHST(unit.basePrice, project) / unit.size) : null}
              </strong>
            </Box>
          </Tooltip>
        {unit.rental && unit.rental !== null && project._id !== '65bd404201d843802bb37a51' && !leasing ? (
          <Tooltip title="Rental Guarantee">
            <Box
              sx={{
                fontStyle: 'em',
                color: '#000',
                fontSize: '9x',
                opacity: selectingUnits && !displayed ? '40%' : '100%',
              }}
            >
              {numToCurrency.format(unit.rental!)}
            </Box>
          </Tooltip>
        ) : null}
        {displayedInfo.allocation && project._id !== '65bd404201d843802bb37a51' && !leasing ? (
          <Tooltip title="Allocation">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
                fontSize: isCompact ? '8px' : '10px',
              }}
            >
              <strong>{unit.allocation ? unit.allocation?.fullName : unit.tempAllocation}</strong>
            </Box>
          </Tooltip>
        ) : null}
        {project._id !== '65bd404201d843802bb37a51' && leasing && unit.lease && unit.lease.management ? (
          <Tooltip title="Rental Management">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
                fontSize: handleFontSize(unit.allocation),
              }}
            >
              <strong>RM</strong>
            </Box>
          </Tooltip>
        ) : null}
        {project._id !== '65bd404201d843802bb37a51' && leasing && unit.lease && unit.lease.listing && unit.lease.listing.dateAvailable ? (
          <Tooltip title="Listing Available Date">
            <Box
              sx={{
                opacity: selectingUnits && !displayed ? '40%' : '100%',
                fontSize: handleFontSize(unit.allocation),
              }}
            >
              <strong>{convertAllDates(unit.lease.listing.dateAvailable, 'PP')}</strong>
            </Box>
          </Tooltip>
        ) : null}
      </Box>
    </div>
  );
};

interface ChildProps {
  unit: IUnit;
  type: String;
  displayed: boolean | undefined;
  selectingUnits: boolean;
  handleSelectedUnits: any;
  draft: boolean;
}

export default UnitGridCard;
